import React, { useState, useCallback, useEffect, lazy, Suspense } from "react";
import "./HomeScreen.css";
import Nav from "../Nav";
import Banner from "../Banner";
import Row from "../Row";
import MiniPlayer from "../components/MiniPlayer";
import BookService from "../services/BookService";
import AudioService from "../services/AudioService";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";

// Lazy load heavy modal components for better initial load
const BookModal = lazy(() => import("../components/BookModal"));
const BookReader = lazy(() => import("../components/BookReader"));
const AudioPlayer = lazy(() => import("../components/AudioPlayer"));
const AuthModal = lazy(() => import("../components/AuthModal"));
const PaywallModal = lazy(() => import("../components/PaywallModal"));

function HomeScreen() {
  const { isPremium } = useAuth();
  const { language } = useLanguage();

  // Country detection for payment provider
  const [isArgentina, setIsArgentina] = useState(true);

  useEffect(() => {
    const detectCountry = async () => {
      // Check for URL override first (for demos)
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('currency') === 'usd') {
        setIsArgentina(false);
        return;
      }

      try {
        const response = await fetch('/api/get-country');
        const data = await response.json();
        setIsArgentina(data.isArgentina);
      } catch (error) {
        console.error('Error detecting country:', error);
        setIsArgentina(true);
      }
    };
    detectCountry();
  }, []);

  // Modal states
  const [selectedItem, setSelectedItem] = useState(null);
  const [showBookModal, setShowBookModal] = useState(false);
  const [showBookReader, setShowBookReader] = useState(false);
  const [showAudioPlayer, setShowAudioPlayer] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showPaywallModal, setShowPaywallModal] = useState(false);

  // MiniPlayer state
  const [miniPlayerState, setMiniPlayerState] = useState(null);


  const handleItemClick = (item) => {
    setSelectedItem(item);

    if (item.type === 'audiobook') {
      setShowAudioPlayer(true);
    } else {
      setShowBookModal(true);
    }
  };

  const handleAvatarClick = () => {
    setShowAuthModal(true);
  };

  const handleCloseBookModal = () => {
    setShowBookModal(false);
  };

  const handleReadBook = (book) => {
    setShowBookModal(false);
    setShowBookReader(true);
  };

  const handleCloseBookReader = () => {
    setShowBookReader(false);
    setSelectedItem(null);
  };

  const handleCloseAudioPlayer = () => {
    setShowAudioPlayer(false);
    setMiniPlayerState(null);
    setSelectedItem(null);
  };

  const handleMinimizePlayer = (state) => {
    setShowAudioPlayer(false);
    setMiniPlayerState(state);
  };

  const handleExpandMiniPlayer = () => {
    setShowAudioPlayer(true);
    setMiniPlayerState(null);
  };

  const handleCloseMiniPlayer = () => {
    setMiniPlayerState(null);
    setSelectedItem(null);
  };

  const handleShowPaywall = () => {
    setShowBookReader(false);
    setShowPaywallModal(true);
  };

  const handleClosePaywall = () => {
    setShowPaywallModal(false);
  };

  const handleSignInForPaywall = () => {
    setShowPaywallModal(false);
    setShowAuthModal(true);
  };

  // Memoized fetch functions - re-fetch when language changes
  const fetchFictionBooks = useCallback(() =>
    BookService.getBooksByCategory('fiction', language), [language]);
  const fetchPoetryBooks = useCallback(() =>
    BookService.getBooksByCategory('poetry', language), [language]);
  const fetchPhilosophyBooks = useCallback(() =>
    BookService.getBooksByCategory('philosophy', language), [language]);
  const fetchAdventureBooks = useCallback(() =>
    BookService.getBooksByCategory('adventure', language), [language]);
  const fetchMysteryBooks = useCallback(() =>
    BookService.getBooksByCategory('mystery', language), [language]);

  const fetchFictionAudio = useCallback(() =>
    AudioService.getAudiobooksByCategory('fiction'), []);

  return (
    <div className="homeScreen">
      <Nav onItemSelect={handleItemClick} onAvatarClick={handleAvatarClick} />

      <Banner
        onPlayClick={handleItemClick}
        onInfoClick={handleItemClick}
      />

      {/* Books Sections - First row loads eagerly for faster viewport */}
      <Row
        title="Fiction"
        fetchFunction={fetchFictionBooks}
        onItemClick={handleItemClick}
        type="book"
        isFirstRow={true}
        adPosition={4}
        isPremium={isPremium}
      />

      <Row
        title="Poetry"
        fetchFunction={fetchPoetryBooks}
        onItemClick={handleItemClick}
        type="book"
        adPosition={8}
        isPremium={isPremium}
      />

      {/* Audiobooks - 3rd row, loads lazily when user scrolls */}
      <Row
        title="Audiobooks"
        fetchFunction={fetchFictionAudio}
        onItemClick={handleItemClick}
        type="audiobook"
        adPosition={8}
        isPremium={isPremium}
      />

      <Row
        title="Philosophy"
        fetchFunction={fetchPhilosophyBooks}
        onItemClick={handleItemClick}
        type="book"
        adPosition={3}
        isPremium={isPremium}
      />

      <Row
        title="Adventure"
        fetchFunction={fetchAdventureBooks}
        onItemClick={handleItemClick}
        type="book"
        adPosition={8}
        isPremium={isPremium}
      />

      <Row
        title="Mystery"
        fetchFunction={fetchMysteryBooks}
        onItemClick={handleItemClick}
        type="book"
        adPosition={5}
        isPremium={isPremium}
      />

      {/* MiniPlayer stays outside Suspense as it's small */}
      {miniPlayerState && (
        <MiniPlayer
          playerState={miniPlayerState}
          onExpand={handleExpandMiniPlayer}
          onClose={handleCloseMiniPlayer}
        />
      )}

      {/* Lazy-loaded modals wrapped in Suspense */}
      <Suspense fallback={null}>
        {showBookModal && (
          <BookModal
            book={selectedItem}
            isOpen={showBookModal}
            onClose={handleCloseBookModal}
            onRead={handleReadBook}
            isPremium={isPremium}
            onShowPaywall={handleShowPaywall}
          />
        )}

        {showBookReader && (
          <BookReader
            book={selectedItem}
            isOpen={showBookReader}
            onClose={handleCloseBookReader}
            isPremium={isPremium}
            onShowPaywall={handleShowPaywall}
          />
        )}

        {showAudioPlayer && (
          <AudioPlayer
            audiobook={selectedItem}
            isOpen={showAudioPlayer}
            onClose={handleCloseAudioPlayer}
            isPremium={isPremium}
            onShowPaywall={handleShowPaywall}
            onMinimize={handleMinimizePlayer}
          />
        )}

        {showAuthModal && (
          <AuthModal
            isOpen={showAuthModal}
            onClose={() => setShowAuthModal(false)}
          />
        )}

        {showPaywallModal && (
          <PaywallModal
            isOpen={showPaywallModal}
            onClose={handleClosePaywall}
            onSignIn={handleSignInForPaywall}
            isArgentina={isArgentina}
          />
        )}
      </Suspense>
    </div>
  );
}

export default HomeScreen;
